import { useState, useMemo, useEffect } from 'react';

/**
 * Simple hook for managing sortable columns
 * @param {Array} data - The data to sort
 * @param {Object} options - Configuration
 * @param {string} options.initialSortBy - Initial column to sort by
 * @param {string} options.initialSortDirection - Initial sort direction ('asc' or 'desc')
 * @param {Object} options.columnSortFunctions - Map of custom sort functions per column
 * @param {string} options.sortBy - Controlled sortBy (if provided, uses controlled mode)
 * @param {string} options.sortDirection - Controlled sortDirection (if provided, uses controlled mode)
 * @param {Function} options.onSortChange - Callback when sort changes (required for controlled mode)
 */
export const useSortableColumns = (data = [], options = {}) => {
  const { initialSortBy = null, initialSortDirection = 'asc', columnSortFunctions = {}, sortBy: controlledSortBy, sortDirection: controlledSortDirection, onSortChange } = options;

  const isControlled = controlledSortBy !== undefined && onSortChange !== undefined;

  const [internalSortBy, setInternalSortBy] = useState(initialSortBy);
  const [internalSortDirection, setInternalSortDirection] = useState(initialSortDirection);

  const sortBy = isControlled ? controlledSortBy : internalSortBy;
  const sortDirection = isControlled ? controlledSortDirection : internalSortDirection;

  // Sync internal state when controlled props change
  useEffect(() => {
    if (isControlled) {
      if (controlledSortBy !== undefined) {
        setInternalSortBy(controlledSortBy);
      }
      if (controlledSortDirection !== undefined) {
        setInternalSortDirection(controlledSortDirection);
      }
    }
  }, [isControlled, controlledSortBy, controlledSortDirection]);

  const handleSort = (column) => {
    if (isControlled) {
      const newDirection = sortBy === column ? (sortDirection === 'asc' ? 'desc' : 'asc') : 'asc';
      onSortChange(column, newDirection);
    } else {
      if (internalSortBy === column) {
        setInternalSortDirection(internalSortDirection === 'asc' ? 'desc' : 'asc');
      } else {
        setInternalSortBy(column);
        setInternalSortDirection('asc');
      }
    }
  };

  const sortedData = useMemo(() => {
    if (!data || data.length === 0) {
      return [];
    }

    if (!sortBy) {
      return data;
    }

    const isEmptyValue = (value) => {
      if (value === null || value === undefined) {
        return true;
      }

      if (typeof value === 'string' && value.trim() === '') {
        return true;
      }

      return false;
    };

    const isNumericLikeString = (value) => {
      if (typeof value !== 'string') {
        return false;
      }

      // Matches optional currency symbol, optional leading minus, digits with optional commas, and an optional decimal part.
      // Examples: "0", "80", "2,000", "-1500.25", "$2,000", "  $2,000.00  "
      const numericPattern = /^-?\s*\$?\s*\d[\d,]*(\.\d+)?\s*$/;

      return numericPattern.test(value);
    };

    const parseNumeric = (value) => {
      if (typeof value === 'number') {
        return value;
      }

      if (typeof value !== 'string') {
        return NaN;
      }

      const cleaned = value.replace(/[^0-9.-]/g, '');

      if (!cleaned) {
        return NaN;
      }

      return Number.parseFloat(cleaned);
    };

    const isDateLike = (value) => {
      if (value instanceof Date) {
        return true;
      }

      if (typeof value !== 'string') {
        return false;
      }

      // If it looks like a plain numeric / currency string, treat it as a number instead of a date.
      if (isNumericLikeString(value)) {
        return false;
      }

      return !Number.isNaN(Date.parse(value));
    };

    const columnSortFn = columnSortFunctions[sortBy];

    const compare = (a, b) => {
      const aVal = a[sortBy];
      const bVal = b[sortBy];

      // Use custom sort function if provided
      if (columnSortFn) {
        return columnSortFn(aVal, bVal);
      }

      // Date comparison
      const aIsDate = isDateLike(aVal);
      const bIsDate = isDateLike(bVal);

      if (aIsDate && bIsDate) {
        return new Date(aVal).getTime() - new Date(bVal).getTime();
      }

      // Numeric comparison (handles numbers and numeric strings, including currency-like strings)
      const aNum = parseNumeric(aVal);
      const bNum = parseNumeric(bVal);

      if (!Number.isNaN(aNum) && !Number.isNaN(bNum)) {
        return aNum - bNum;
      }

      // String comparison fallback
      return String(aVal).localeCompare(String(bVal));
    };

    // Split rows into those with values and those without so that "empty" values
    // are always shown at the bottom, regardless of sort direction.
    const rowsWithValue = [];
    const rowsWithoutValue = [];

    for (const row of data) {
      const value = row[sortBy];

      if (isEmptyValue(value)) {
        rowsWithoutValue.push(row);
      } else {
        rowsWithValue.push(row);
      }
    }

    const sortedWithValue = rowsWithValue.sort(compare);

    if (sortDirection === 'desc') {
      sortedWithValue.reverse();
    }

    return [...sortedWithValue, ...rowsWithoutValue];
  }, [data, sortBy, sortDirection, columnSortFunctions]);

  return {
    sortedData,
    sortBy,
    sortDirection,
    handleSort,
    isSorted: (col) => sortBy === col
  };
};

export default useSortableColumns;
